import { Type, Index } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

const MapValueError = (error) => ({
    message: error.message,
    path: error.path,
    errors: error.errors.map(iterator => mapValueErrorIterator(iterator))
});
const mapValueErrorIterator = (iterator) => [...iterator].map(error => MapValueError(error));

class DataLayerError extends Error {
    constructor({ message, errors = [] }) {
        super(message);
        this.message = message;
        this.validationErrors = mapValueErrorIterator(errors);
    }
}

/**
 * ATTENTION. This code was AUTO GENERATED by schema2typebox.
 * While I don't know your use case, there is a high chance that direct changes
 * to this file get lost. Consider making changes to the underlying JSON schema
 * you use to generate this file instead. The default file is called
 * "schema.json", perhaps have a look there! :]
 */
const types = Type.Object({
    pageData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "pageData", readOnly: true })),
        href: Type.String(),
        pagePath: Type.String(),
        pageTitle: Type.String(),
        pageName: Type.Optional(Type.String()),
        pageType: Type.Optional(Type.String()),
        siteContentHierarchy: Type.Optional(Type.String()),
        viewportDimensions: Type.Union([
            Type.String({ pattern: "^\\d*\\:\\d*$" }),
            Type.Null({ pattern: "^\\d*\\:\\d*$" }),
        ]),
        localTimestamp: Type.String({
            pattern: "^([\\+-]?\\d{4}(?!\\d{2}\\b))((-?)((0[1-9]|1[0-2])(\\3([12]\\d|0[1-9]|3[01]))?|W([0-4]\\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\\d|[12]\\d{2}|3([0-5]\\d|6[1-6])))([T\\s]((([01]\\d|2[0-3])((:?)[0-5]\\d)?|24\\:?00)([\\.,]\\d+(?!:))?)?(\\17[0-5]\\d([\\.,]\\d+)?)?([zZ]|([\\+-])([01]\\d|2[0-3]):?([0-5]\\d)?)?)?)?$",
            $comment: "timestamp to match iso format, e.g. 2000-01-00T00:00:00.839Z",
        }),
        hostname: Type.String(),
        queryString: Type.String(),
        browseCurrency: Type.String(),
        browseCountry: Type.String(),
        language: Type.Optional(Type.String()),
        channel: Type.Union([
            Type.Literal("women"),
            Type.Literal("men"),
            Type.Literal("default"),
            Type.Literal("checkout"),
        ], { $schema: "http://json-schema.org/draft-07/schema#" }),
        deviceType: Type.Union([
            Type.Literal("desktop"),
            Type.Literal("mobile"),
            Type.Literal("tablet"),
        ]),
        env: Type.Union([Type.Literal("test"), Type.Literal("prod")]),
        referringUrl: Type.String(),
        marketingData: Type.Optional(Type.Object({
            affid: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            nlid: Type.Optional(Type.Union([
                Type.String({
                    description: "Navigation link identifier (nlid) for tracking navigation source.",
                }),
                Type.Null({
                    description: "Navigation link identifier (nlid) for tracking navigation source.",
                }),
            ])),
            googleCid: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            gclid: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            ranSiteId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            ranLandingDate: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            ranMid: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            awinId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            admitadId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            fbAdId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            fbLandingDate: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            impactRadiusClickId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            asosConnectTransactionId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            buttonId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            crealyticsId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            intelligentReachId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            publisherReference: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            zanoxPartnerId: Type.Optional(Type.Union([Type.String(), Type.Null()])),
        })),
        customerData: Type.Optional(Type.Object({
            isAuthenticated: Type.Optional(Type.Boolean()),
            customerId: Type.Optional(Type.String()),
            firstTimeBuyer: Type.Optional(Type.Boolean()),
            premierStatus: Type.Optional(Type.Boolean()),
            studentStatus: Type.Optional(Type.Boolean()),
            hashedEmail: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            hashedFirstName: Type.Optional(Type.String()),
            hashedLastName: Type.Optional(Type.String()),
            hashedPhoneNumber: Type.Optional(Type.Union([Type.String(), Type.Null()])),
            geoIpCountryCode: Type.Optional(Type.String()),
            asosWorldTier: Type.Optional(Type.String()),
            customerSegments: Type.Optional(Type.Array(Type.String())),
        })),
        webVersion: Type.Optional(Type.String()),
        ctaRef: Type.Optional(Type.String()),
        pageComponents: Type.Optional(Type.Array(Type.String())),
        attributionCategory: Type.Optional(Type.String()),
    }, {
        description: "The pageData event is triggered on every page load or navigation change, delivering essential context about the current page. It provides initial data required for rendering, analytics, and other page-specific logic.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/pageData.json",
        additionalProperties: false,
    })),
    customerPremierData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerPremierData", readOnly: true })),
        premierStatus: Type.String(),
        freeTrialAvailable: Type.Boolean(),
    }, {
        description: "The customerPremierData event is triggered when customer Premier data is retrieved by the consuming app. It provides data that pertains to the customers current Premier status and free trial eligibility.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerPremierData.json",
        additionalProperties: false,
    })),
    customerSegmentsData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerSegmentsData", readOnly: true })),
        segments: Type.Array(Type.String()),
    }, {
        description: "The customerSegmentsData event is triggered when customer segments data is retrieved by the consuming app. It provides the list of customer segments as a comma separated string.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerSegmentsData.json",
        additionalProperties: false,
    })),
    customerLoyaltyData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerLoyaltyData", readOnly: true })),
        consentStatus: Type.Union([Type.String(), Type.Null()]),
        currentTierId: Type.Union([Type.String(), Type.Null()]),
        tierNumber: Type.Union([Type.Number(), Type.Null()]),
        country: Type.Union([Type.String(), Type.Null()]),
    }, {
        description: "The customerLoyaltyData event is triggered when customer loyalty data is retrieved by the consuming app. It provides the customer's loyalty status, tier ID, tier number and customer's country.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerLoyaltyData.json",
        additionalProperties: false,
    })),
    customerStudentData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerStudentData", readOnly: true })),
        isStudent: Type.Boolean(),
    }, {
        description: "The customerStudentData event is triggered when the student status is retrieved by the consuming app. It provides the student status as a boolean value.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerStudentData.json",
        additionalProperties: false,
    })),
    OneTrustGroupsUpdated: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "OneTrustGroupsUpdated", readOnly: true })),
        OnetrustActiveGroups: Type.String(),
    }, {
        description: "The OneTrustGroupsUpdated event is triggered whenever customer consent changes. It provides a list of groups that indicate the ways a customer has permitted their personal data to be used.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/OneTrustGroupsUpdated.json",
        additionalProperties: false,
    })),
    timeToFirstByteData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "timeToFirstByte", readOnly: true })),
        value: Type.Number(),
    }, {
        description: "The timeToFirstByte event is triggered when the time to first byte has finished being calculated. It contains vital information to understanding the page performance.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/timeToFirstByteEvent.json",
        additionalProperties: false,
    })),
    firstContentfulPaintData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "firstContentfulPaint", readOnly: true })),
        value: Type.Number(),
    }, {
        description: "The firstContentfulPaintData event is triggered when the first contentful paint timing has finished being calculated. It contains vital information to understanding the page performance.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/firstContentfulPaintEvent.json",
        additionalProperties: false,
    })),
    add_shipping_info: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_shipping_info", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            shipping_tier: Type.Optional(Type.String({
                description: "The shipping tier of the selected option (e.g. Standard Shipping, Next-Day, Express)",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
        section: Type.Optional(Type.String({ default: "deliveryOptions", readOnly: true })),
        deliveryOptionId: Type.Optional(Type.Number()),
        isSplitFulfilment: Type.Optional(Type.Boolean({
            description: "True if some items will be delivered by a different option to the one selected.",
        })),
    }, {
        description: "The add_shipping_info event is triggered when selection of a delivery option is made through explicitly clicking the associated radio button. Indicates that a delivery option has been explicitly selected by the customer and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_shipping_info.json",
    })),
    begin_checkout: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "begin_checkout", readOnly: true })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The begin_checkout event is triggered entry / re-entry to Checkout / refresh of checkout page. It provides information about the items in the basket and their total value, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/begin_checkout.json",
        additionalProperties: false,
    })),
    add_to_cart: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_to_cart", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
    }, {
        description: "The add_to_cart event is triggered when a customer clicks on add to bag. It provides information about the product(s) added to the bag and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_to_cart.json",
    })),
    view_item: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_item", readOnly: true })),
        productId: Type.Number(),
        productCode: Type.String(),
        availableSku: Type.Optional(Type.Number()),
        availableSizes: Type.Optional(Type.Number()),
        productUrl: Type.String(),
        cid: Type.Union([Type.Number(), Type.Null()]),
        category: Type.Optional(Type.String({
            description: "RMS Product Type Clean breakdown of all products onsite / category for filtering on site. This is optional only in the case of a M&M product.",
        })),
        variantStockStatus: Type.Optional(Type.Array(Type.Object({
            variantId: Type.Number(),
            variantSku: Type.String(),
            stockStatus: Type.String(),
        }, {
            description: "The stock status of each variant of the product",
            additionalProperties: false,
        }))),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_item is triggered on entry / re-entry / refresh of single PDP or Mix and Match PDP Browse application.  It provides information about the product and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/ViewItem.json",
        additionalProperties: false,
    })),
    view_item_list: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_item_list", readOnly: true })),
        item_list_name: Type.Union([
            Type.Literal("search"),
            Type.Literal("brand"),
            Type.Literal("category"),
            Type.Literal("wishlist"),
            Type.Literal("plp_carousel"),
            Type.Literal("pdp_ymal"),
            Type.Literal("pdp_pab"),
            Type.Literal("bag_pab"),
            Type.Literal("pdp_btl"),
            Type.Literal("brand_directory"),
        ], {
            description: "Type of listing page. For search pages this is always 'search'. For brand pages this is always 'brand'. For brand directory pages this is always 'brand_directory'. For category pages this is always 'category'. For saved list items this is always 'wishlist'. For any component level lists, it should be one of the following values: 'plp_carousel', 'pdp_ymal', 'pdp_pab', 'bag_pab', 'pdp_btl'",
        }),
        item_list_state: Type.Optional(Type.Union([
            Type.Literal("search"),
            Type.Literal("failed_search"),
            Type.Literal("category"),
            Type.Literal("generic_search"),
            Type.Literal("pagination"),
            Type.Literal("refinement"),
            Type.Literal("sort"),
            Type.Literal("sort_and_refinement"),
        ], {
            description: "The state that the list is in, essential for analytics tracking on category & search pdps\nsearch - Indicates the list has loaded immedietly after the user has done a search\nfailed_search - Indicates the list is a search page with no results\ncategory - Indicates the list is a normal category page\ngeneric_search - Indicates the list is a search page, but not invoked by the searchTerm\npagination - Indicates the list has been paginated\nrefinement - Indicates the list has been refined\nsort - Indicates the list has been sorted\nsort_and_refinement - Indicates the list has been sorted and refined",
        })),
        search_term: Type.Optional(Type.String()),
        searchType: Type.Optional(Type.String({
            description: "How the customer performed the search",
        })),
        categoryID: Type.Optional(Type.Number()),
        pageGridSize: Type.Optional(Type.Number({
            description: "The number of product displayed in each column",
        })),
        pageNumber: Type.Optional(Type.Number({ description: "The page number where applicable" })),
        pageSize: Type.Optional(Type.Number({
            description: "The number of products displayed on each page",
        })),
        totalStyles: Type.Optional(Type.Number({
            description: "The total number of products, not just the ones displayed",
        })),
        floor: Type.Optional(Type.Union([
            Type.Literal("men"),
            Type.Literal("women"),
            Type.Literal("search"),
            Type.Literal("default"),
        ])),
        breadCrumb: Type.Optional(Type.String({
            pattern: ".*[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+%\\$[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+(%\\$[A-Za-zÀ-ÖØ-öø-ÿ0-9 &']+)*.*",
            description: "String containing at least one '%$'-separated segment, e.g., 'women%$new' or 'women%$a to z of brands%$asos design'.",
        })),
        refinements: Type.Optional(Type.Object({
            context: Type.Optional(Type.String({
                description: "The refinements the customer used, where applicable",
            })),
            listAppliedRefinements: Type.Optional(Type.Array(Type.Object({
                refinementId: Type.Optional(Type.String({ description: "Facet ID" })),
                refinementName: Type.Optional(Type.String({ description: "Facet name" })),
                selections: Type.Optional(Type.Array(Type.Object({
                    selectionId: Type.Optional(Type.String({ description: "Selection ID" })),
                    selectionName: Type.Optional(Type.String({ description: "Selection name" })),
                }))),
            }))),
            sort: Type.Optional(Type.Object({
                sortName: Type.Optional(Type.String({ description: "Sort name" })),
                sortType: Type.Optional(Type.String({ description: "Sort option" })),
            })),
        })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_item_list event is triggered when a list of items is viewed by the customer. It provides the list of items for different lists (search, brand pages, category pages, wishlists) and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/ViewItemListData.json",
        additionalProperties: false,
    })),
    view_cart: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "view_cart", readOnly: true })),
        totalItemCount: Type.Number(),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The view_cart event is triggered on entry / re-entry / refresh of Bag application. It provides the total number of items in the cart, item values and the cart value, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/view_cart.json",
        additionalProperties: false,
    })),
    add_payment_info: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_payment_info", readOnly: true })),
        ecommerce: Type.Optional(Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            payment_type: Type.Optional(Type.String({
                description: "Payment method currently selected for use with the order.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({
                    description: "The category of the item.",
                })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        })),
        section: Type.Optional(Type.String({ default: "paymentOptions", readOnly: true })),
        isSavedPayment: Type.Optional(Type.Boolean({
            description: "Indicates that the user has previously provided the payment method.",
        })),
    }, {
        description: "The add_payment_info event is triggered when a payment method is selected from the list of payments. It indicates successful entry of a payment method either through a selection of a saved payment method or entry of a new payment method, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_payment_info.json",
    })),
    customerIdentityData: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customerIdentityData", readOnly: true })),
        customerId: Type.Optional(Type.String()),
        firstTimeBuyer: Type.Optional(Type.Boolean()),
        geoIpCountryCode: Type.String(),
        hashedFirstName: Type.Optional(Type.String()),
        hashedLastName: Type.Optional(Type.String()),
        hashedEmail: Type.Optional(Type.String()),
        hashedPhoneNumber: Type.Optional(Type.String()),
        isAuthenticated: Type.Boolean(),
    }, {
        description: "The customerIdentityData event is triggered when the customer identity data is retrieved by the consuming app. It provides the customer's identity data.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/customerIdentityData.json",
        additionalProperties: false,
    })),
    add_to_wishlist: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "add_to_wishlist", readOnly: true })),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The add_to_wishlist event is triggered when a customer clicks on a product tile's save for later CTA. It provides information about the product(s) added to the wishlist and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/add_to_wishlist.json",
        additionalProperties: false,
    })),
    purchase: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "purchase", readOnly: true })),
        page: Type.Optional(Type.String({ default: "orderConfirmed", readOnly: true })),
        context: Type.Optional(Type.Object({
            dateTimePair: Type.Optional(Type.String({
                pattern: "^([\\+-]?\\d{4}(?!\\d{2}\\b))((-?)((0[1-9]|1[0-2])(\\3([12]\\d|0[1-9]|3[01]))?|W([0-4]\\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\\d|[12]\\d{2}|3([0-5]\\d|6[1-6])))([T\\s]((([01]\\d|2[0-3])((:?)[0-5]\\d)?|24\\:?00)([\\.,]\\d+(?!:))?)?(\\17[0-5]\\d([\\.,]\\d+)?)?([zZ]|([\\+-])([01]\\d|2[0-3]):?([0-5]\\d)?)?)?)?$",
                description: "The date and time at which the order occurred. In ISO8601 format",
            })),
        })),
        order: Type.Object({
            orderReference: Type.Optional(Type.String({ description: "The order reference number." })),
            currency: Type.Optional(Type.String({
                description: "ISO 4216 of currency customer is shopping in.",
            })),
            sizeSchema: Type.Optional(Type.String({
                description: "Size schema customer used during customer journey.",
            })),
            totalSalesTax: Type.Optional(Type.Number({
                minimum: 0,
                description: "Total amount of sales tax payable by the customer (if applicable).",
            })),
            totalSalesTaxGbp: Type.Optional(Type.Number({
                minimum: 0,
                description: "Sales tax converted to GBP (if applicable).",
            })),
            totalPrice: Type.Optional(Type.Number({
                minimum: 0,
                description: "The total price of the bag (ItemsSubtotal + Delivery - Discount + TotalSalesTax).",
            })),
            totalPriceGbp: Type.Optional(Type.Number({
                minimum: 0,
                description: "The total price of the bag in GBP.",
            })),
            discountCode: Type.Optional(Type.Object({
                code: Type.Optional(Type.String({ description: "Discount code used." })),
                totalAmount: Type.Optional(Type.Number({ minimum: 0, description: "Total discount." })),
                totalAmountGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Total discount in GBP.",
                })),
            }, { description: "optional data if discount is used." })),
            delivery: Type.Optional(Type.Object({
                deliveryCountryIsoCode: Type.Optional(Type.String({ description: "3166-1 alpha-2 code." })),
                deliveryOptionId: Type.Optional(Type.Number({
                    description: "The integer id of the selected delivery option.",
                })),
                isSplitFulfilment: Type.Optional(Type.Boolean({
                    description: "True if some of the items on the order will be delivered on a different delivery to the one represented by the deliveryOptionId.",
                })),
                price: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The price of the delivery (exclusive of sales tax where it is explicitly paid).",
                })),
                priceGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The price of the delivery (exclusive of sales tax where it is explicitly paid) in GBP.",
                })),
                salesTax: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Sales tax applied on delivery (if applicable).",
                })),
                salesTaxGbp: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "Sales tax in GBP applied on delivery (if applicable).",
                })),
            })),
            payment: Type.Optional(Type.Object({
                billingCity: Type.Optional(Type.String({
                    description: "Payment method used to pay for the order.",
                })),
                billingCountryIsoCode: Type.Optional(Type.String({
                    description: "The ISO 3166-1 alpha-2 code of the selected billing country.",
                })),
                billingPostCode: Type.Optional(Type.String({
                    description: "The post code of the billing address.",
                })),
                billingState: Type.Optional(Type.String({ description: "State or County." })),
                paymentMethods: Type.Optional(Type.Array(Type.Object({
                    paymentMethod: Type.String({
                        description: "Method of payment.",
                    }),
                    cardScheme: Type.Optional(Type.String({
                        description: "Card type used if paid by card.",
                    })),
                    voucherType: Type.Optional(Type.Union([
                        Type.Literal("Loyalty"),
                        Type.Literal("Gift card"),
                        Type.Literal("Standard"),
                        Type.Literal("Out of policy return"),
                    ])),
                    amount: Type.Optional(Type.Number({
                        description: "Voucher amount used in order.",
                    })),
                    amountRedeemable: Type.Optional(Type.Number({
                        description: "Amount redeemable PRIOR to this order.",
                    })),
                }, {
                    $schema: "http://json-schema.org/draft-07/schema#",
                    additionalProperties: false,
                }), {
                    description: "The payment methods used to pay for the order.",
                })),
            })),
        }),
        ecommerce: Type.Object({
            currency: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Currency code. Always “GBP”.",
                default: "GBP",
                readOnly: true,
            })),
            localCurrencyCode: Type.Optional(Type.String({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Local currency code of the store user is browsing.",
            })),
            value: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in GBP.",
            })),
            valueLocalCurrency: Type.Optional(Type.Number({
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "Total value of basket in local currency.",
            })),
            payment_type: Type.Optional(Type.String({
                description: "Payment method currently selected for use with the order.",
            })),
            transaction_id: Type.Optional(Type.String({
                description: "Order reference number of purchase.",
            })),
            coupon: Type.Optional(Type.String({
                description: "Discount code associated with the purchase (if applicable).",
            })),
            discount: Type.Optional(Type.Number({
                description: "The discount applied to the item in GBP.",
            })),
            discountLocalCurrency: Type.Optional(Type.Number({
                description: "The discount applied to the item in local currency.",
            })),
            shipping: Type.Optional(Type.Number({
                description: "Shipping cost in GBP (if applicable).",
            })),
            shippingLocalCurrency: Type.Optional(Type.Number({
                description: "Shipping cost in local currency (if applicable).",
            })),
            tax: Type.Optional(Type.Number({ description: "Tax cost in GBP (if applicable)." })),
            taxLocalCurrency: Type.Optional(Type.Number({
                description: "Tax cost in local currency (if applicable).",
            })),
            items: Type.Optional(Type.Array(Type.Object({
                item_id: Type.Optional(Type.String({
                    description: "Mapped from variantSku. item_id or item_name is required",
                })),
                productId: Type.Optional(Type.String({ description: "Mapped from productIID." })),
                productCode: Type.Optional(Type.String({ description: "Mapped from ProductCode." })),
                variantId: Type.Optional(Type.String({
                    description: "Mapped from productVariantId.",
                })),
                item_name: Type.Optional(Type.String({
                    description: "The name of the item. item_name or item_id is required.",
                })),
                discount: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in GBP. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                discountLocalCurrency: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The discount applied to the item in local currency. This is specifically the discount from coupons and promo codes and is unrelated to sale prices.",
                })),
                index: Type.Optional(Type.Number({
                    description: "The position of the item in a list (e.g., search results, product list). This value should start at 1.",
                })),
                item_brand: Type.Optional(Type.String({ description: "The brand of the item." })),
                item_category: Type.Optional(Type.String({ description: "The category of the item." })),
                item_variant: Type.Optional(Type.String({
                    description: "The specific variant of the item.",
                })),
                price: Type.Optional(Type.Number({
                    description: "The price of the item in GBP. Mandatory wherever possible",
                    minimum: 0,
                })),
                priceLocalCurrency: Type.Optional(Type.Number({
                    description: "The price of the item in browse local currency. Mandatory wherever possible",
                    minimum: 0,
                })),
                quantity: Type.Optional(Type.Number({
                    minimum: 0,
                    description: "The quantity of the item.",
                })),
                coupon: Type.Optional(Type.String({
                    description: "The discount code being used for the discount.",
                })),
                size: Type.Optional(Type.String({ description: "The size of the item" })),
                itemType: Type.Optional(Type.Union([
                    Type.Literal("Product"),
                    Type.Literal("Subscription"),
                    Type.Literal("Voucher"),
                ], {
                    description: "The item type, either Product, Subscription or Voucher",
                })),
                google_business_vertical: Type.Optional(Type.String({
                    default: "retail",
                    readOnly: true,
                    description: "Always 'retail'. Used for dynamic remarketing, indicates the business vertical of the product.",
                })),
            }, {
                $schema: "http://json-schema.org/draft-07/schema#",
                description: "The item is a fundamental part of the GA4 ecommerce data layer. This should align to https://asoscom.atlassian.net/wiki/spaces/MARTECH/pages/5636096001/ASOS.com+Marketing+Data+Layer+GA4+Schema#Items",
                additionalProperties: false,
            }))),
        }),
    }, {
        description: "The purchase event is triggered on loading of the order accepted/received page or refreshing of the order accepted/received page. It indicates that the customer has loaded the order confirmation page after successful order placement, and follows GA4 schema: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list with additional custom ASOS fields.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/purchase.json",
        additionalProperties: false,
    })),
    homepage_available_components: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({
            default: "homepage_available_components",
            readOnly: true,
        })),
        components: Type.Array(Type.Object({
            position: Type.Number(),
            component_name: Type.String(),
        })),
    }, {
        description: "A list of components that will load on the homepage",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/homepage_available_components.json",
        additionalProperties: false,
    })),
    customVariables: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "customVariables", readOnly: true })),
        customVariables: Type.Object({
            "1": Type.Object({
                name: Type.String({ default: "countryCode" }),
                value: Type.String(),
            }),
            "3": Type.Object({
                name: Type.String({ default: "asosDeviceType" }),
                value: Type.String(),
            }),
            "4": Type.Object({
                name: Type.String({ default: "storeCode" }),
                value: Type.String(),
            }),
            "9": Type.Object({
                name: Type.String({ default: "pageType" }),
                value: Type.String(),
            }),
        }, { additionalProperties: false }),
    }, {
        description: "ContentSquare specific custom variables to describe the page to the datalayer",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/content_square_custom_variables.json",
        additionalProperties: false,
    })),
    failed_event: Type.Optional(Type.Object({
        event: Type.Optional(Type.String({ default: "failed_event", readOnly: true })),
        errors: Type.Optional(Type.Array(Type.Unknown())),
        event_context: Type.Optional(Type.Unknown()),
    }, {
        description: "The failed_event is triggered when an event fails to process correctly. It provides information about the error and the context for the event.",
        $schema: "http://json-schema.org/draft-07/schema#",
        $id: "https://asos.com/dataLayer/events/failed_event.json",
    })),
}, {
    $schema: "http://json-schema.org/draft-07/schema#",
    $id: "https://asos.com/dataLayer/events.json",
});

const dataLayerEventKeys = Object.values(types.properties).map((schema) => schema.properties.event.default);

function getWindow() {
    return globalThis.window;
}

const pushToDataLayerSitechromeWrapper = async (definitions) => {
    var _a, _b;
    const { pushToDataLayer } = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.siteChrome) !== null && _b !== void 0 ? _b : {};
    if (pushToDataLayer) {
        return await pushToDataLayer(definitions);
    }
    return new Promise((resolve, reject) => {
        var _a, _b, _c;
        const timeoutId = setTimeout(() => {
            reject(new Error("Timeout: pushToDataLayer-ready event was not fired within 10 seconds"));
        }, 10000);
        (_c = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.eventBus) === null || _b === void 0 ? void 0 : _b.once) === null || _c === void 0 ? void 0 : _c.call(_b, "pushToDataLayer-ready", async () => {
            var _a, _b, _c;
            clearTimeout(timeoutId);
            try {
                const result = await ((_c = (_b = (_a = getWindow().asos) === null || _a === void 0 ? void 0 : _a.siteChrome) === null || _b === void 0 ? void 0 : _b.pushToDataLayer) === null || _c === void 0 ? void 0 : _c.call(_b, definitions));
                resolve(result);
            }
            catch (e) {
                reject(e);
            }
        });
    });
};

const defaults = (eventDefinition, eventType) => Value.Default(Index(types, [eventType]), eventDefinition);

const clean = (eventDefinition, eventType) => Value.Clean(Index(types, [eventType]), eventDefinition);

const validate = (eventDefinition, eventType) => {
    const errors = [...Value.Errors(Index(types, [eventType]), eventDefinition)];
    if (errors.length) {
        throw new DataLayerError({
            message: `Object is not a valid match to the ${eventType} schema`,
            errors
        });
    }
};

const handleFailedEvent = async (dataLayerSink, logError, eventDefinition, error) => {
    if (eventDefinition.event === "failed_event") {
        return undefined;
    }
    error.message = `${error.message}: See full error.validationErrors for details`;
    logError(error);
    return processEvent("failed_event", {
        failed_event: {
            event_context: eventDefinition,
            errors: (error === null || error === void 0 ? void 0 : error.validationErrors) || []
        }
    }, dataLayerSink, logError);
};

const processEvent = async (eventKey, definitions, dataLayerSink, logError) => {
    let eventDefinition = defaults(definitions[eventKey], eventKey);
    eventDefinition = clean(eventDefinition, eventKey);
    try {
        validate(eventDefinition, eventKey);
        await dataLayerSink({
            events: [eventDefinition.event],
            data: eventDefinition
        });
        return eventDefinition;
    }
    catch (error) {
        return await handleFailedEvent(dataLayerSink, logError, eventDefinition, error);
    }
};

const pushToDataLayer = async ({ logError, dataLayerSink }, definitions) => {
    if (!dataLayerSink) {
        logError(new DataLayerError({ message: "Datalayer sink is not setup" }));
        return null;
    }
    const processedDefinitions = [];
    for (const eventKey of Object.keys(definitions)) {
        const result = await processEvent(eventKey, definitions, dataLayerSink, logError);
        if (result !== undefined) {
            processedDefinitions.push(result);
        }
    }
    return processedDefinitions;
};

export { DataLayerError, dataLayerEventKeys, pushToDataLayer, pushToDataLayerSitechromeWrapper };
//# sourceMappingURL=index.js.map
